import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  LANGUAGE_STORAGE_KEY,
  getLanguageConfig
} from '../config/languages';
import { getFromStorage, setInStorage } from '../utils/storage';
import { detectBrowserLanguage } from '../utils/language-detection';
import {
  getDefaultLanguage,
  getSupportedLanguages,
  isLanguageSupported,
  getCurrentTenant
} from '../config/tenants';

const LanguageContext = createContext(null);

function extractLangFromPath(pathname, supportedLanguages) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && supportedLanguages.includes(segments[0])) {
    return segments[0];
  }
  return null;
}

function stripLangPrefix(pathname, supportedLanguages) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && supportedLanguages.includes(segments[0])) {
    const remaining = segments.slice(1).join('/');
    return remaining ? `/${remaining}` : '/';
  }
  return pathname;
}

export function LanguageProvider({ children }) {
  const tenant = getCurrentTenant();
  const tenantDefaultLanguage = getDefaultLanguage();
  const supportedLanguages = getSupportedLanguages();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [language, setLanguageState] = useState(() => {
    const urlLang = extractLangFromPath(window.location.pathname, supportedLanguages);
    if (urlLang) return urlLang;

    const stored = getFromStorage(LANGUAGE_STORAGE_KEY);
    if (stored && isLanguageSupported(stored)) {
      return stored;
    }

    const detected = detectBrowserLanguage();
    if (isLanguageSupported(detected)) {
      return detected;
    }

    return tenantDefaultLanguage;
  });

  // Sync language when URL changes
  useEffect(() => {
    const urlLang = extractLangFromPath(location.pathname, supportedLanguages);
    if (urlLang && urlLang !== language) {
      setLanguageState(urlLang);
      setInStorage(LANGUAGE_STORAGE_KEY, urlLang);
      queryClient.invalidateQueries();
    }
  }, [location.pathname]);

  const setLanguage = useCallback((newLanguage) => {
    if (!isLanguageSupported(newLanguage)) {
      console.warn(`Language "${newLanguage}" not supported by ${tenant.name}. Using ${tenantDefaultLanguage}.`);
      newLanguage = tenantDefaultLanguage;
    }

    setLanguageState(newLanguage);
    setInStorage(LANGUAGE_STORAGE_KEY, newLanguage);

    if (supportedLanguages.length > 1) {
      const basePath = stripLangPrefix(location.pathname, supportedLanguages);
      const newPath = basePath === '/' ? `/${newLanguage}` : `/${newLanguage}${basePath}`;
      navigate(newPath + location.search);
    }

    queryClient.invalidateQueries();
  }, [navigate, location.pathname, location.search, supportedLanguages, tenant.name, tenantDefaultLanguage, queryClient]);

  const value = {
    language,
    setLanguage,
    languageConfig: getLanguageConfig(language),
    supportedLanguages,
    tenant
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
}
